import React from 'react'
import { Popover } from 'antd'
import EpcCycleIcon from './EpcCycleIcons'
import EpcCyclePopover, { IoPill, stageName } from './EpcCyclePopover'
import {
  ACTION_CONFIG,
  STAGE_CONFIG,
  colorVars,
  daysBetween,
  dirsOf,
  eventsOf,
  groupStages,
  isIoStage,
  stageColor
} from './epcCycleUtils'

const STEP_DELAY = 0.28

const dateRange = (first, last) => {
  const start = first.time
  const end = last.endTime || last.time
  if (!start && !end) return '-'
  if (!start || !end) return (start || end).slice(0, 10)
  return start.slice(0, 10) === end.slice(0, 10) ? start.slice(0, 10) : `${start.slice(5, 10)} ~ ${end.slice(5, 10)}`
}

/**
 * 单个 EPC 的图标时间线（含循环组件、事件标签）；浮层的打开状态由页面统一控制，保证同时只开一个
 */
const EpcCycleFlow = ({ t, card, cardIndex, baseDelay, activePop, setActivePop, popClassName, getPopupContainer }) => {
  const stages = card.stages || []
  const lastIdx = stages.length - 1
  const groups = groupStages(stages)

  const popover = (key, content, child) => (
    <Popover
      key={key}
      open={activePop === key}
      onOpenChange={open => setActivePop(open ? key : null)}
      trigger="click"
      placement="bottom"
      arrow={false}
      destroyTooltipOnHide
      rootClassName={popClassName}
      getPopupContainer={getPopupContainer}
      content={content}
    >
      {child}
    </Popover>
  )

  const close = () => setActivePop(null)

  const renderEvents = (list, keyBase, delay) => {
    const chips = Object.keys(ACTION_CONFIG)
      .map(kind => {
        const evs = eventsOf(stages, list, kind)
        if (!evs.length) return null
        const a = ACTION_CONFIG[kind]
        const key = `${keyBase}:${kind}`
        return popover(
          key,
          <EpcCyclePopover t={t} card={card} list={list} kind={kind} onClose={close} />,
          <div
            className={`evt${activePop === key ? ' active' : ''}${kind === 'REPRINT' ? ' reprint' : ''}`}
            style={{ '--c': a.color, animationDelay: `${delay}s` }}
          >
            <EpcCycleIcon name={a.icon} />
            {t(`event.${a.localeKey}`)}
            {evs.length > 1 ? ` ×${evs.length}` : ''}
          </div>
        )
      })
      .filter(Boolean)
    return chips.length ? <div className="evts">{chips}</div> : null
  }

  const renderStep = (type, list, isCurrent, delay, keyBase, evDelay) => {
    const config = STAGE_CONFIG[type] || {}
    const first = stages[list[0]]
    const last = stages[list[list.length - 1]]
    const dirs = dirsOf(stages, list)
    const io = isIoStage(type)
    const kinds = [...new Set(eventsOf(stages, list).map(x => x.ev.kind))]
    const badge = list.length > 1 ? `×${list.length}` : list[0] + 1
    const key = `${keyBase}:stage`
    const range = dateRange(first, last)
    const sub = list.length > 1 ? t('times', { n: list.length }) : first.location || '-'
    return (
      <div
        className="step"
        key={keyBase}
        style={{
          ...colorVars(config.color || stageColor(type)),
          ...(delay != null ? { animationDelay: `${delay}s` } : {})
        }}
      >
        {popover(
          key,
          <EpcCyclePopover t={t} card={card} list={list} onClose={close} />,
          <div className={`icon${isCurrent ? ' current' : ''}${activePop === key ? ' active' : ''}`}>
            <EpcCycleIcon name={config.icon || 'dc'} />
            <span className={`no${list.length > 1 ? ' times' : ''}`}>{badge}</span>
            {kinds.length ? (
              <span
                className={`act-dot${kinds[0] === 'REPRINT' ? ' reprint' : ''}`}
                style={{ color: ACTION_CONFIG[kinds[0]].color }}
              >
                <EpcCycleIcon name={ACTION_CONFIG[kinds[0]].icon} />
              </span>
            ) : null}
          </div>
        )}
        {io ? <IoPill dirs={dirs} /> : null}
        <div className="name" style={io ? { marginTop: 4 } : undefined}>
          {stageName(t, type, dirs)}
        </div>
        <div className="sub">
          {sub}
          <br />
          {range}
        </div>
        {renderEvents(list, keyBase, (evDelay != null ? evDelay : delay || 0) + 0.35)}
      </div>
    )
  }

  const renderLoop = (g, gi, delay) => {
    const [ta, tb] = g.pair
    const ca = stageColor(ta)
    const cb = stageColor(tb)
    const markerId = `epc-loop-${cardIndex}-${gi}`
    const isCur = type => stages[lastIdx].stageType === type && lastIdx >= g.first && lastIdx <= g.last
    const keyBase = `${cardIndex}:${gi}`
    return (
      <div className="loop" key={keyBase} style={{ animationDelay: `${delay}s` }}>
        <svg className="loop-svg" viewBox="0 0 300 56">
          <defs>
            <marker
              id={`${markerId}b`}
              viewBox="0 0 10 10"
              refX="7"
              refY="5"
              markerWidth="4"
              markerHeight="4"
              orient="auto"
            >
              <path d="M0 0L10 5L0 10z" fill={cb} />
            </marker>
            <marker
              id={`${markerId}a`}
              viewBox="0 0 10 10"
              refX="7"
              refY="5"
              markerWidth="4"
              markerHeight="4"
              orient="auto"
            >
              <path d="M0 0L10 5L0 10z" fill={ca} />
            </marker>
          </defs>
          <path className="arc" d="M92 22 C130 -20, 170 -20, 208 22" stroke={cb} markerEnd={`url(#${markerId}b)`} />
          <path className="arc" d="M208 36 C170 76, 130 76, 92 36" stroke={ca} markerEnd={`url(#${markerId}a)`} />
        </svg>
        {renderStep(ta, g.a, isCur(ta), null, `${keyBase}:a`, delay)}
        <div className="loop-center">
          <div className="spin">
            <EpcCycleIcon name="loop" />
          </div>
          <div className="cnt" style={{ background: cb }}>
            {t('returns', { n: g.b.length })}
          </div>
        </div>
        {renderStep(tb, g.b, isCur(tb), null, `${keyBase}:b`, delay)}
      </div>
    )
  }

  const parts = []
  groups.forEach((g, gi) => {
    const startType = stages[g.first].stageType
    if (gi > 0) {
      const prevLast = stages[groups[gi - 1].last]
      const d = baseDelay + gi * STEP_DELAY - 0.14
      const days = daysBetween(prevLast.endTime || prevLast.time, stages[g.first].time)
      parts.push(
        <div className="gap-label" key={`gap-${gi}`}>
          <div className="d" style={{ animationDelay: `${d}s`, visibility: days == null ? 'hidden' : 'visible' }}>
            +{days == null ? 0 : days}d
          </div>
          <div
            className="link"
            style={{ '--from': stageColor(prevLast.stageType), '--to': stageColor(startType), animationDelay: `${d}s` }}
          />
        </div>
      )
    }
    const delay = baseDelay + gi * STEP_DELAY
    parts.push(
      g.loop ? renderLoop(g, gi, delay) : renderStep(startType, g.idx, g.first === lastIdx, delay, `${cardIndex}:${gi}`)
    )
  })

  return <div className="flow">{parts}</div>
}

export default EpcCycleFlow
